import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { useCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type Employee from 'modules/employees/store/models/Employee'
import YearNavigation from 'modules/timeoff/components/YearNavigation'
import Months from 'modules/timeoff/components/YearlyCalendar/Months'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import { FACTORIAL_CREATION_YEAR } from 'modules/timeoff/lib/constants'
import { useContractsVersion } from 'modules/timeoff/lib/contractsVersionContext'
import { BlockedPeriods } from 'modules/timeoff/store/collections/blockedPeriods/blockedPeriods'
import { Leaves } from 'modules/timeoff/store/collections/leaves'

import CalendarStore from '../../YearlyCalendar/Months/store'
import Legend from './Legend'
import SkeletonLoader from './SkeletonLoader'

interface Props {
  employee: Employee
  year: number
  canCreateLeaves?: boolean
  canApproveLeaves?: boolean
}

const Calendar = ({ employee, year, canCreateLeaves, canApproveLeaves }: Props) => {
  const { employee: currentEmployee } = useCurrentSession()
  const { startsOn } = useContractsVersion()
  const [currentYear, setCurrentYear] = React.useState(year)
  const [loading, setLoading] = React.useState(false)
  const calendarLeaves: Leaves = useLazyRef(() => new Leaves()).current
  const calendarBlockedPeriods: BlockedPeriods = useLazyRef(() => new BlockedPeriods()).current
  const currentAndFutureLeavesContextValue = useLeaves()

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const actionLabel = canApproveLeaves ? i18n.t('time_off.admin.action') : i18n.t('time_off.action')

  const isOwnTimeoff = (id: number): boolean => {
    return currentEmployee?.id === id
  }

  const onClick = () => {
    const id = employee.attributes.id
    navigate(
      isOwnTimeoff(id)
        ? navigation.timeOff.leavesNew.fullPath()
        : navigation.employees.id.timeOff.leavesNew.fullPath({ id })
    )
  }

  const employeeLeaves = useCallback(
    () => {
      return calendarLeaves.filter({
        employee_id: employee.attributes.id,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const employeeBlockedPeriods = useCallback(
    () => {
      return calendarBlockedPeriods.toArray()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const fetchData = useCallback(
    async (targetYear: number) => {
      setLoading(true)

      // There is a special case for those allowance using natural days. If the
      // first day of the year is during the weekend and there is a leave on the
      // last day of the previous year, we need to get this leave in order to
      // display it properly in this year
      const firstDayOfYear = moment([targetYear, 0, 1])
      const firstDayWeekday = firstDayOfYear.isoWeekday()
      const isFirstDayWeekend = [6, 7].includes(firstDayWeekday)
      const fromDate = isFirstDayWeekend
        ? firstDayOfYear.subtract(firstDayWeekday - 5, 'days')
        : firstDayOfYear
      await Promise.all([
        calendarLeaves.fetch({
          data: {
            employee_id: employee.attributes.id,
            terminated: true,
            from: dateToString(fromDate, 'backend'),
            to: dateToString(moment([targetYear, 11, 31]), 'backend'),
          },
        }),
        calendarBlockedPeriods.fetch({
          data: {
            employee_id: employee.attributes.id,
            start_on: dateToString(fromDate, 'backend'),
            finish_on: dateToString(moment([targetYear, 11, 31]), 'backend'),
          },
        }),
      ])
      setCurrentYear(targetYear)
      setLoading(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  useEffect(() => {
    // Fetches the leaves to display in the calendar whenever there's a change
    // in the context's currentAndFutureLeaves (because the user updated a
    // leave for example)
    fetchData(currentYear)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, currentAndFutureLeavesContextValue.fetchCount])

  const maxYear = moment().year() + 1
  const minYear = (startsOn && startsOn.year()) || FACTORIAL_CREATION_YEAR

  const store = useMemo(() => {
    return new CalendarStore({
      year: currentYear,
      leaves: employeeLeaves(),
      blockedPeriods: employeeBlockedPeriods(),
    })
  }, [currentYear, employeeLeaves().length, employeeBlockedPeriods().length])

  if (loading) return <SkeletonLoader />

  return (
    <Box padding="s32" gap="s32">
      <Box justifyContent="spaceBetween" flexDirection="row">
        <YearNavigation
          onChange={fetchData}
          type="quaternary"
          year={currentYear}
          minYear={minYear}
          maxYear={maxYear}
        />
        {canCreateLeaves && (
          <Button onClick={onClick} type="primary" iconName="add">
            {actionLabel}
          </Button>
        )}
      </Box>
      <Box>
        <Months dateTree={store.getTree} year={currentYear} />
      </Box>
      <Box>
        <Legend employeeLeaves={employeeLeaves()} blockedPeriods={employeeBlockedPeriods()} />
      </Box>
    </Box>
  )
}

export default Calendar
